import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { rem } from 'polished'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { trackEvent } from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import { useAppSelector } from 'hooks/reduxHooks'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { isMaldivesCharterCarouselEnabled } from 'selectors/maldivesSelectors'

const MALDIVES_BEST_DEALS_PATH = `/campaign/${CHARTER_PAGE_SLUG}`
const PROMOTION_NAME = 'Maldives Our Best Deals Ever Homepage Banner'

const DESKTOP_MIN_HEIGHT = 160
const TABLET_MIN_HEIGHT = 120
const MOBILE_IMAGE_HEIGHT = 117
const DESKTOP_IMAGE_WIDTH = 370
const DESKTOP_ARC_SIZE = 246
const DESKTOP_ARC_LEFT = 55
const DESKTOP_ARC_TOP = -67

const DesktopCta = styled.span`
  display: none;
  flex: none;
  align-items: center;
  justify-content: center;
  min-width: ${rem(80)};
  padding: ${rem(12)} ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.staticWhite};
  color: ${props => props.theme.palette.highlight.primary.normalContrast};

  ${mediaQueryUp.desktop} {
    display: inline-flex;
  }
`

const Banner = styled(ClickableLink)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.XL};
  background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
  color: ${props => props.theme.palette.highlight.primary.darkContrast};
  text-decoration: none;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: stretch;
    min-height: ${rem(TABLET_MIN_HEIGHT)};
  }

  ${mediaQueryUp.desktop} {
    min-height: ${rem(DESKTOP_MIN_HEIGHT)};
  }

  ${mediaHoverable} {
    &:hover ${DesktopCta} {
      background-color: color-mix(in srgb, ${props => props.theme.palette.neutral.surface.staticWhite} 88%, ${props => props.theme.palette.highlight.primary.normalContrast});
    }
  }
`

const ImageFrame = styled.div`
  position: relative;
  flex: none;
  height: ${rem(MOBILE_IMAGE_HEIGHT)};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    flex: 0 0 37%;
    width: 37%;
    height: auto;
    align-self: stretch;
  }

  ${mediaQueryUp.desktop} {
    flex-basis: ${rem(DESKTOP_IMAGE_WIDTH)};
    width: ${rem(DESKTOP_IMAGE_WIDTH)};
  }
`

const BannerPhoto = styled(Image)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`

/* Cream arc from the key visual. Percentages track the image frame until the desktop width is fixed. */
const Arc = styled.div`
  position: absolute;
  width: 66%;
  aspect-ratio: 1;
  inset-inline-start: 10%;
  top: -122%;
  border: ${rem(1.5)} solid ${props => props.theme.palette.neutral.surface.lightBackground};
  border-start-start-radius: 65%;
  border-start-end-radius: 65%;
  border-end-end-radius: 65%;
  border-end-start-radius: 4%;
  transform: rotate(-90deg);
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    inset-inline-start: 14%;
    top: auto;
    bottom: 8%;
    border-width: ${rem(2)};
  }

  ${mediaQueryUp.desktop} {
    width: ${rem(DESKTOP_ARC_SIZE)};
    inset-inline-start: ${rem(DESKTOP_ARC_LEFT)};
    top: ${rem(DESKTOP_ARC_TOP)};
    bottom: auto;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    flex: 1;
    justify-content: center;
    gap: ${rem(8)};
    padding: ${rem(20)} ${rem(28)};
  }

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(24)};
    padding: ${rem(20)} ${rem(40)};
  }
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(4)};
  min-width: 0;

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    flex: 1;
  }
`

const BannerHeading = styled(Heading)`
  && {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.5px;
  }
`

const CompactCta = styled(BodyText)`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

function MaldivesBestDealsHomepageBanner() {
  const intl = useIntl()
  const isEnabled = useAppSelector(isMaldivesCharterCarouselEnabled)
  const [inViewRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })

  const labelText = intl.formatMessage({
    id: 'q5I1ZfnEl5AO6gArsFom',
    defaultMessage: 'Limited Time Only',
    description: 'Promotional label on the Maldives best deals homepage banner',
  })

  const headingText = intl.formatMessage({
    id: 'ePZC5ipZxTzGp8rj1rIk',
    defaultMessage: 'Our Best Maldives Deals Ever',
    description: 'Headline on the Maldives best deals promotional banner on the homepage',
  })

  const ctaText = intl.formatMessage({
    id: 'CiCs03TGwqOOA6BvEl38',
    defaultMessage: 'Book now',
    description: 'Call to action on the Maldives best deals homepage banner, linking to the campaign',
  })

  const accessibleLabel = intl.formatMessage({
    id: '8TajQQQ3Y6qwdJG88L6D',
    defaultMessage: 'Our Best Maldives Deals Ever. Limited time only. Book now.',
    description: 'Accessible name for the Maldives best deals promotional banner link on the homepage',
  })

  useEffect(() => {
    if (!inView) {
      return
    }

    trackEvent(promotionViewEvent({
      promotionContext: {
        promotionName: PROMOTION_NAME,
        promotionType: 'banner',
      },
    }))
  }, [inView])

  const handleClick = useCallback(() => {
    trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: PROMOTION_NAME,
        promotionType: 'banner',
      },
    }))
  }, [])

  if (!isEnabled || isAgentHubEnabled) {
    return null
  }

  return <Banner ref={inViewRef} to={MALDIVES_BEST_DEALS_PATH} aria-label={accessibleLabel} onClick={handleClick}>
    <ImageFrame>
      <BannerPhoto id="maldives-best-deals-banner-img" fit="center" alt="" />
      <Arc aria-hidden="true" />
    </ImageFrame>
    <Content>
      <Copy>
        <HighlightLabel>{labelText}</HighlightLabel>
        <BannerHeading variant="heading3" colour="highlight-primary-dark-contrast">{headingText}</BannerHeading>
      </Copy>
      <DesktopCta>
        <BodyText variant="large" as="span">{ctaText}</BodyText>
      </DesktopCta>
      <CompactCta variant="medium" weight="bold" underline as="span">{ctaText}</CompactCta>
    </Content>
  </Banner>
}

export default MaldivesBestDealsHomepageBanner
